import { useDroppable } from '@dnd-kit/core';
import classNames from 'classnames';
import { ChangeEvent, FC, ReactElement, useCallback, useMemo, useState, useEffect } from 'react';
import { Icons } from 'src/assets/fonts/Icons/icons';
import theme from 'src/constants/styles';
import { branchStore } from 'src/mobx/branchStore';
import { IBranch } from 'src/types/branches';
import { isTouchDevice } from 'src/utils/helpers';
import Icon from 'src/view/components/Controls/Icon/Icon';
import { TProfile } from '~/view/containers/ProfilesList';
import './style.scss';

interface IProps {
  profile: TProfile;
  selectProfile: (profile: TProfile) => void;
  onUpdateName?: (profile: TProfile) => Promise<boolean | undefined> | boolean | undefined;
  selectedProfileId?: number | null;
}

export const Profile: FC<IProps> = ({ profile, selectedProfileId, ...props }: IProps): ReactElement => {
  const { isOver, active: isDroppable, setNodeRef } = useDroppable({ id: String(profile.id) });
  const [isEditMode, setEditMode] = useState<boolean>(false);
  const [profileName, setProfileName] = useState<string>(profile.name);
  const [hover, setHover] = useState<boolean>(false);

  const isSelected = useMemo((): boolean => selectedProfileId === profile.id, [selectedProfileId, profile.id]);
  const _isTouchDevice = useMemo((): boolean => isTouchDevice(), []);

  useEffect(() => {
    if (!isSelected) setEditMode(false);
    return () => setProfileName(profile.name);
  }, [isSelected]);

  const isEditButtonVisible = useMemo((): boolean => {
    if (profile.is_default) return false;
    return _isTouchDevice && isSelected ? true : hover;
  }, [profile.is_default, _isTouchDevice, isSelected, hover]);

  const numberOfBranches = useMemo((): number => {
    return branchStore.branches.reduce((sum: number[], current: IBranch) => {
      return sum.concat(profile.branches?.some((item) => item.id === current.id) ? current.id : []);
    }, []).length;
  }, [profile.branches, branchStore.branches]);

  const selectProfile = useCallback(() => {
    if (selectedProfileId === profile.id) return;
    props.selectProfile(profile);
  }, [selectedProfileId, profile]);

  const changeName = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setProfileName(event.target.value);
  }, []);

  const saveName = async () => {
    if (!props.onUpdateName) return;
    const response: boolean | undefined = await props.onUpdateName({ ...profile, name: profileName });
    if (response) {
      setEditMode(false);
    } else {
      setProfileName(profile.name);
    }
  };

  const itemClassName = useMemo((): string => {
    const isEditOrSelectedDroppable: boolean = isEditMode || Boolean(isSelected && isDroppable);

    return classNames('profile', {
      profile_active: !isEditOrSelectedDroppable && (isSelected || isOver),
      profile_editing: isEditOrSelectedDroppable && profileName,
      'input-error': isEditOrSelectedDroppable && !profileName,
    });
  }, [isEditMode, isSelected, isDroppable, isOver, profileName]);

  return (
    <li
      id={`profile_${profile.id}`}
      ref={setNodeRef}
      onClick={selectProfile}
      onPointerEnter={_isTouchDevice ? undefined : () => setHover(true)}
      onPointerLeave={_isTouchDevice ? undefined : () => setHover(false)}
      className={itemClassName}
    >
      {isEditMode ? (
        <>
          <input
            type="text"
            value={profileName}
            onChange={changeName}
            className="input profile__input family-semibold text-size-16 color-dark m-3 text-elipsis"
          />
          <button onClick={saveName} disabled={!profileName} className="profile__btn-save m-1 p-0">
            <Icon name={Icons.Ok} size={18} color={theme.colors.dark} className="mx-auto" />
          </button>
        </>
      ) : (
        <>
          <div className="profile__title-wrapper">
            <p className="profile__title family-semibold text-size-16 color-dark my-3 ml-3">{profile.name}</p>
            {profile?.branches !== undefined ? (
              <span className="family-semibold text-size-16 color-gray2 ml-1">{`(${numberOfBranches})`}</span>
            ) : null}
          </div>

          {isEditButtonVisible ? (
            <button onClick={() => setEditMode(true)} className="profile__btn-change">
              <Icon name={Icons.Pencil} size={16} color={theme.colors.mainblue} />
            </button>
          ) : null}
        </>
      )}
    </li>
  );
};
